import { useState, useEffect } from 'react';
import { pb, getRestaurantId } from '../config/pocketbase';

// Fallback predefiniti locali per garantire rendering istantaneo a latenza zero
const DEFAULT_BRANDS = {
  'default': {
    slug: 'default',
    nome: 'Pizzeria Le Cannucceta',
    sottotitolo: 'Pizzeria Tradizionale a Legna',
    colore_primario: '#5C715E',
    icona_svg: `<svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="100" height="100" rx="24" fill="#5C715E"/><circle cx="50" cy="50" r="32" stroke="#FFFFFF" stroke-width="4"/><path d="M35 50L45 60L65 40" stroke="#FFFFFF" stroke-width="5" stroke-linecap="round"/></svg>`
  },
  'ristorante-1': {
    slug: 'ristorante-1',
    nome: 'Pizzeria Le Cannucceta',
    sottotitolo: 'Pizzeria Tradizionale a Legna',
    colore_primario: '#5C715E',
    icona_svg: `<svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="100" height="100" rx="24" fill="#5C715E"/><circle cx="50" cy="50" r="32" stroke="#FFFFFF" stroke-width="4"/><path d="M35 50L45 60L65 40" stroke="#FFFFFF" stroke-width="5" stroke-linecap="round"/></svg>`
  },
  'ristorante-2': {
    slug: 'ristorante-2',
    nome: 'Bella Napoli & Burger',
    sottotitolo: 'Pizzeria & Smash Burger',
    colore_primario: '#E94E3C',
    icona_svg: `<svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="100" height="100" rx="24" fill="#E94E3C"/><circle cx="50" cy="50" r="30" fill="#FFC107"/><text x="50%" y="60%" font-size="32" font-weight="900" fill="#FFFFFF" text-anchor="middle">BN</text></svg>`
  },
  'osteria-da-nonna-rosa': {
    slug: 'osteria-da-nonna-rosa',
    nome: 'Osteria Da Nonna Rosa',
    sottotitolo: 'Cucina Tradizionale & Primi',
    colore_primario: '#8A584C',
    icona_svg: `<svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="100" height="100" rx="24" fill="#8A584C"/><text x="50%" y="60%" font-size="32" font-weight="900" fill="#FFFFFF" text-anchor="middle">NR</text></svg>`
  }
};

/**
 * Genera un SVG Data URL personalizzato per la favicon del ristorante
 */
const generateFaviconDataUrl = (nome = 'Tap2Order', colore = '#5C715E') => {
  const initials = nome
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0].toUpperCase())
    .join('') || 'T2';

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect width="64" height="64" rx="16" fill="${colore}"/><text x="50%" y="58%" font-family="system-ui, -apple-system, sans-serif" font-weight="900" font-size="28" fill="#FFFFFF" text-anchor="middle" dominant-baseline="middle">${initials}</text></svg>`;
  
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

/**
 * Aggiorna dinamicamente la Favicon e il Title nel documento HTML
 */
const updateDocumentHead = (nome, colore) => {
  if (typeof document === 'undefined') return;

  // 1. Aggiorna Document Title
  document.title = `${nome} — Menù & Ordini`;

  // 2. Aggiorna Favicon
  const faviconUrl = generateFaviconDataUrl(nome, colore);
  let link = document.querySelector("link[rel*='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.getElementsByTagName('head')[0].appendChild(link);
  }
  link.type = 'image/svg+xml';
  link.href = faviconUrl;
};

/**
 * Hook per il caricamento reattivo del brand del ristorante
 */
export const useRestaurantBrand = () => {
  const slug = getRestaurantId() || 'default';
  const initialBrand = DEFAULT_BRANDS[slug] || {
    slug,
    nome: slug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    sottotitolo: 'Ristorante & Pizzeria',
    colore_primario: '#5C715E'
  };

  const [brand, setBrand] = useState(initialBrand);

  useEffect(() => {
    let isMounted = true;

    // Aggiornamento immediato head browser
    updateDocumentHead(brand.nome, brand.colore_primario);

    // Fetch da PocketBase per recuperare personalizzazioni registrate
    const fetchBrand = async () => {
      try {
        const records = await pb.collection('ristoranti_tap2order').getList(1, 1, {
          filter: `slug = "${slug}"`,
          requestKey: null
        });

        if (isMounted && records.items.length > 0) {
          const pbBrand = records.items[0];
          const updated = {
            slug: pbBrand.slug,
            nome: pbBrand.nome || initialBrand.nome,
            sottotitolo: pbBrand.sottotitolo || initialBrand.sottotitolo,
            colore_primario: pbBrand.colore_primario || initialBrand.colore_primario,
            logo_url: pbBrand.logo_url,
            favicon_url: pbBrand.favicon_url,
            icona_svg: pbBrand.icona_svg
          };
          setBrand(updated);
          updateDocumentHead(updated.nome, updated.colore_primario);
        }
      } catch (e) {
        // Nessun record remoto, usa fallback locale senza bloccare l'interfaccia
      }
    };

    fetchBrand();

    return () => {
      isMounted = false;
    };
  }, [slug]);

  return brand;
};

export default useRestaurantBrand;
